/**
 * quick-reply-vars.ts — Biến {{…}} trong câu trả lời nhanh.
 *
 * Cú pháp bám đúng backend `automation/template-renderer.ts` (mẫu tin dùng chung
 * bảng với Tự động hoá) để một mẫu viết một lần chạy được ở cả hai nơi. Ở ô soạn
 * tin, biến được thay NGAY trên trình duyệt bằng dữ liệu hội thoại đang mở;
 * biến không có dữ liệu được giữ nguyên để nhân viên thấy và sửa tay.
 */

import { i18n } from '@/lib/i18n'

export interface QuickReplyVariable {
  key: string
  label: string
}

/** Nhãn mô tả theo ngôn ngữ hiện tại (khoá `key` là tên biến thật, giữ nguyên). */
export function quickReplyVariables(): QuickReplyVariable[] {
  return [
    { key: 'contact.firstName', label: i18n.t('quickReplies:vars.firstName') },
    { key: 'contact.name', label: i18n.t('quickReplies:vars.fullName') },
    { key: 'contact.phone', label: i18n.t('quickReplies:vars.phone') },
    { key: 'contact.email', label: i18n.t('quickReplies:vars.email') },
    { key: 'user.name', label: i18n.t('quickReplies:vars.userName') },
    { key: 'org.name', label: i18n.t('quickReplies:vars.orgName') },
    { key: 'date', label: i18n.t('quickReplies:vars.date') },
  ]
}

export interface QuickReplyContext {
  contact?: { fullName?: string | null; phone?: string | null; email?: string | null } | null
  user?: { fullName?: string | null; email?: string | null } | null
  org?: { name?: string | null } | null
  now?: Date
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g

/** Tên gọi kiểu Việt: lấy chữ cuối của họ tên. */
function firstName(v?: string | null): string {
  const parts = (v ?? '').trim().split(/\s+/).filter(Boolean)
  return parts[parts.length - 1] ?? ''
}

function lookup(key: string, ctx: QuickReplyContext): string {
  const now = ctx.now ?? new Date()
  const c = ctx.contact
  switch (key) {
    case 'date':
      return now.toLocaleDateString('vi-VN')
    case 'time':
      return now.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
    case 'name':
    case 'contact.firstName':
      return firstName(c?.fullName)
    case 'contact.name':
    case 'contact.fullName':
      return c?.fullName?.trim() ?? ''
    case 'contact.phone':
      return c?.phone ?? ''
    case 'contact.email':
      return c?.email ?? ''
    case 'user.name':
    case 'user.fullName':
      return ctx.user?.fullName ?? ''
    case 'user.email':
      return ctx.user?.email ?? ''
    case 'company':
    case 'org.name':
      return ctx.org?.name ?? ''
    default:
      return ''
  }
}

/** Thay biến bằng dữ liệu thật; biến lạ / thiếu dữ liệu giữ nguyên `{{…}}`. */
export function renderQuickReply(content: string, ctx: QuickReplyContext): string {
  return content.replace(PLACEHOLDER_RE, (match, key: string) => lookup(key, ctx) || match)
}
